import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  const fetchList = useCallback(async () => {
    if (!user) { setItems([]); return; }
    try {
      const { data } = await api.get("/wishlist");
      setItems(data);
    } catch (err) {
      console.error("Failed to load wishlist:", err);
    }
  }, [user]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const isWishlisted = useCallback((id) => items.some((p) => p.id === id), [items]);

  const toggle = useCallback(async (product) => {
    if (!user) { toast.error("Please sign in to use your wishlist"); return; }
    if (items.some((p) => p.id === product.id)) {
      await api.delete(`/wishlist/${product.id}`);
      setItems((cur) => cur.filter((p) => p.id !== product.id));
      toast("Removed from wishlist");
    } else {
      await api.post(`/wishlist/${product.id}`);
      setItems((cur) => [...cur, product]);
      toast.success("Saved to wishlist");
    }
  }, [user, items]);

  const value = useMemo(
    () => ({ items, isWishlisted, toggle, fetchList }),
    [items, isWishlisted, toggle, fetchList],
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export const useWishlist = () => useContext(WishlistContext);
